import React, {
  cloneElement,
  ReactElement,
  UIEvent,
  useCallback,
  useState,
} from 'react'
import { Box, Typography } from '@mui/material'
import Breadcrumbs, {
  BreadcrumbsProps,
} from 'src/components/molecules/Breadcrumbs'
import QRCodeModal, { Section } from 'src/components/molecules/QRCodeModal'
import { TabsProps } from 'src/components/molecules/Tabs'
import TabsWithLine from 'src/components/molecules/TabsWithLine'
import StoreTenantHeader, {
  StoreTenantHeaderProps,
} from 'src/components/organisms/StoreTenantHeader'
import Loading from 'src/components/templates/Loading'
import {
  AdminAdminSalesReportManagerEditorSalesReportManagerEditorStoreManagerInOperationCenterRoles,
  isOneOfRoles,
} from 'src/domain/role'
import { RoleEnum } from 'src/slices/services/api'
import { BREADCRUMB_HEIGHT, TABS_WITH_LINE_HEIGHT } from 'src/styles/constants'
import style from './styles.module.scss'

export type TenantDetailProps = {
  breadcrumbPaths: BreadcrumbsProps['paths']
  tabOptions: TabsProps['options']
  tabIndex: number
  tabContents: ReactElement[]
  isLoading?: boolean
  modalTitle: string
  qrSrc: string
  qrSize: number
  modalOpen: boolean
  downloadButtonText: string
  disableBackdropClick?: boolean
  qrPdfElementId: string
  sections: Section[]
  role?: RoleEnum
  onSelectTab: (index: number) => void
  onClickDownload: (elementId: string) => void
  onClickClose: () => void
} & StoreTenantHeaderProps

export type Status = 'ready' | 'not_ready' | 'closed'

const TenantDetail: React.StyledFC<TenantDetailProps> = ({
  tenantName,
  tenantCode,
  status,
  breadcrumbPaths,
  tabOptions,
  tabIndex,
  tabContents,
  isLoading = false,
  showButtonText,
  modalOpen,
  disableBackdropClick,
  modalTitle,
  downloadButtonText,
  qrPdfElementId,
  qrSrc,
  qrSize,
  sections,
  role,
  onSelectTab,
  onClickShowModal,
  onClickDownload,
  onClickClose,
}: TenantDetailProps) => {
  const [showsUnderline, setShowsUnderline] = useState(false)

  const handleClick = (index: number) => {
    onSelectTab(index)
  }

  const handleScroll = (e: UIEvent) => {
    // タイトルのスペース(74px)
    setShowsUnderline(e.currentTarget.scrollTop > 74 + BREADCRUMB_HEIGHT)
  }

  const showSwitchByRole = useCallback((): boolean => {
    return isOneOfRoles(
      AdminAdminSalesReportManagerEditorSalesReportManagerEditorStoreManagerInOperationCenterRoles,
      role
    )
  }, [role])

  return (
    <Box className={style.root} onScroll={handleScroll}>
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <Breadcrumbs className={style.breadcrumb} paths={breadcrumbPaths} />
          <StoreTenantHeader
            className={style.header}
            tenantName={tenantName}
            tenantCode={tenantCode}
            status={status}
            showButtonText={showButtonText}
            onClickShowModal={onClickShowModal}
          />
          {showSwitchByRole() && (
            <>
              <TabsWithLine
                className={style.tab}
                options={tabOptions}
                selectedIndex={tabIndex}
                onClick={(index) => handleClick(index)}
              />
              <Box className={style.tabContents} data-testid="tabContents">
                {cloneElement(tabContents[tabIndex], {
                  showsUnderline,
                  stickyTop: TABS_WITH_LINE_HEIGHT + BREADCRUMB_HEIGHT,
                })}
              </Box>
            </>
          )}
          {!showSwitchByRole() && (
            <Box className={style.message}>
              <Typography variant="h2">
                テナント認証用QRコードの発行について
              </Typography>
              <Typography variant="body1">
                右上の「QRコード表示」ボタンを押下すると
              </Typography>
              <Typography variant="body1">
                テナント認証用QRコードの表示・ダウンロードを行うことができます。
              </Typography>
            </Box>
          )}
        </>
      )}
      <QRCodeModal
        className={style.modal}
        open={modalOpen}
        title={modalTitle}
        disableBackdropClick={disableBackdropClick}
        buttonText={downloadButtonText}
        sections={sections}
        id={qrPdfElementId}
        src={qrSrc}
        size={qrSize}
        onClickDownload={() => onClickDownload(qrPdfElementId)}
        onClickClose={onClickClose}
      />
    </Box>
  )
}

export default TenantDetail
